import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { AlertCircleIcon, InfoIcon, type LucideIcon } from 'lucide-react';

interface FrameProps {
  label: string;
  labelAux?: string;
  error?: string;
  help?: ComponentChildren;
  /** Context the customer can do without, behind an info icon beside the label. */
  hint?: string;
  /** The control itself, already wrapped in whatever positioning element it needs. */
  children: ComponentChildren;
  /** Omit only for a radiogroup, which has no single control to point at and names itself
   *  through `aria-label` — a `<label for>` with no match labels nothing. */
  htmlFor?: string;
}

/** The frame every labelled control shares, so it can't drift apart between them. */
export function FieldFrame({ label, labelAux, error, help, hint, children, htmlFor }: FrameProps) {
  const labelNode =
    htmlFor === undefined ? (
      <span className="sm-type-body sm-label">{label}</span>
    ) : (
      <label className="sm-type-body sm-label" htmlFor={htmlFor}>
        {label}
      </label>
    );

  const labelGroup =
    hint === undefined ? (
      labelNode
    ) : (
      <span className="sm-label-group">
        {labelNode}
        <InfoTip label={label} text={hint} />
      </span>
    );

  return (
    <div className="sm-stack sm-field">
      {labelAux !== undefined || hint !== undefined ? (
        <div className="sm-field-row">
          {labelGroup}
          {labelAux !== undefined && <span className="sm-type-caption sm-tabular">{labelAux}</span>}
        </div>
      ) : (
        labelNode
      )}
      {children}
      {error !== undefined ? (
        <FieldError text={error} />
      ) : help !== undefined ? (
        <p className="sm-type-body sm-muted">{help}</p>
      ) : null}
    </div>
  );
}

/** Hover shows it for a mouse; a tap or Enter pins it until the next press anywhere in the widget. */
function InfoTip({ label, text }: { label: string; text: string }) {
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;

  useEffect(() => {
    const button = buttonRef.current;
    if (!pinned || button === null) return;
    // The root is the shadow root in production, so this sees real targets rather than the host.
    const root = button.getRootNode();
    const unpin = (e: Event) => {
      if (!button.contains(e.composedPath()[0] as Node)) setPinned(false);
    };
    root.addEventListener('pointerdown', unpin);
    return () => root.removeEventListener('pointerdown', unpin);
  }, [pinned]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="sm-center sm-focus-ring sm-info"
        aria-label={`About ${label}`}
        aria-describedby={id}
        onPointerEnter={(e) => e.pointerType === 'mouse' && setHovered(true)}
        onPointerLeave={(e) => e.pointerType === 'mouse' && setHovered(false)}
        onClick={() => !hovered && setPinned((p) => !p)}
        onBlur={() => setPinned(false)}
        onKeyDown={(e) => e.key === 'Escape' && setPinned(false)}
      >
        <InfoIcon size={14} aria-hidden="true" />
      </button>
      <span id={id} role="tooltip" className="sm-type-body sm-info-bubble" hidden={!open}>
        {text}
      </span>
    </>
  );
}

export function FieldError({ text }: { text: string }) {
  return (
    <p className="sm-type-body sm-error">
      <AlertCircleIcon className="sm-error-icon" aria-hidden="true" />
      <span>{text}</span>
    </p>
  );
}

type NativeInputProps = Omit<JSX.IntrinsicElements['input'], 'class' | 'className'>;

type FieldProps = NativeInputProps & {
  label: string;
  labelAux?: string;
  help?: ComponentChildren;
  hint?: string;
  error?: string;
  leadingIcon?: LucideIcon;
  inputClassName?: string;
  trailingSlot?: ComponentChildren;
  id: string;
};

export function Field({
  label,
  labelAux,
  help,
  hint,
  error,
  leadingIcon: LeadingIcon,
  inputClassName,
  trailingSlot,
  id,
  ...inputProps
}: FieldProps) {
  const inputClasses = [
    'sm-control',
    'sm-input',
    LeadingIcon !== undefined ? 'sm-input-leading' : null,
    inputClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FieldFrame
      label={label}
      labelAux={labelAux}
      error={error}
      help={help}
      hint={hint}
      htmlFor={id}
    >
      <div className="sm-input-wrap">
        {LeadingIcon !== undefined && (
          <LeadingIcon className="sm-input-leading-icon" size={16} aria-hidden="true" />
        )}
        <input
          id={id}
          className={inputClasses}
          aria-invalid={error !== undefined || undefined}
          {...inputProps}
        />
        {trailingSlot}
      </div>
    </FieldFrame>
  );
}
